/**
 * The tablet's own database (IndexedDB, in the app's private storage): what it needs to work with
 * no data, and every record waiting to be uploaded. Records leave it only once the server has
 * accepted them, so running out of data, a reload or a restart never loses one.
 */

/** A record kept until the server accepts it. `body` is what the sync API takes. */
export interface OutboxEntry {
  readonly id: string;
  readonly kind: 'session' | 'cycle' | 'event';
  readonly body: Record<string, unknown>;
  /** rejected = the server will never take it; kept for a person to look at. */
  readonly state: 'pending' | 'rejected';
  readonly reason?: string;
  readonly createdAt: string;
}

export interface LocalStore {
  get<T>(key: string): Promise<T | undefined>;
  put(key: string, value: unknown): Promise<void>;
  remove(key: string): Promise<void>;
  /** Adds or replaces (by id) a record waiting to upload. */
  addToOutbox(entry: OutboxEntry): Promise<void>;
  outbox(): Promise<OutboxEntry[]>;
  removeFromOutbox(ids: readonly string[]): Promise<void>;
}

const DB_NAME = 'evesix';
const KV = 'kv';
const OUTBOX = 'outbox';

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function committed(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));
  });
}

/** IndexedDB, opened on first use. Writes ask for strict durability: flushed before they count. */
export class IdbStore implements LocalStore {
  #db: Promise<IDBDatabase> | null = null;
  readonly #factory: IDBFactory;

  constructor(factory: IDBFactory = indexedDB) {
    this.#factory = factory;
  }

  #open(): Promise<IDBDatabase> {
    this.#db ??= new Promise((resolve, reject) => {
      const request = this.#factory.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(KV);
        request.result.createObjectStore(OUTBOX, { keyPath: 'id' });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return this.#db;
  }

  async #write(store: string, work: (s: IDBObjectStore) => void): Promise<void> {
    const tx = (await this.#open()).transaction(store, 'readwrite', { durability: 'strict' });
    work(tx.objectStore(store));
    await committed(tx);
  }

  async get<T>(key: string): Promise<T | undefined> {
    const tx = (await this.#open()).transaction(KV, 'readonly');
    return (await done(tx.objectStore(KV).get(key))) as T | undefined;
  }

  put(key: string, value: unknown): Promise<void> {
    return this.#write(KV, (s) => s.put(value, key));
  }

  remove(key: string): Promise<void> {
    return this.#write(KV, (s) => s.delete(key));
  }

  addToOutbox(entry: OutboxEntry): Promise<void> {
    return this.#write(OUTBOX, (s) => s.put(entry));
  }

  async outbox(): Promise<OutboxEntry[]> {
    const tx = (await this.#open()).transaction(OUTBOX, 'readonly');
    const all = (await done(tx.objectStore(OUTBOX).getAll())) as OutboxEntry[];
    return all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  removeFromOutbox(ids: readonly string[]): Promise<void> {
    return this.#write(OUTBOX, (s) => {
      for (const id of ids) s.delete(id);
    });
  }
}

/** In memory: tests, and builds with no IndexedDB. Loses everything on reload. */
export class MemoryStore implements LocalStore {
  readonly #kv = new Map<string, unknown>();
  readonly #outbox = new Map<string, OutboxEntry>();

  get<T>(key: string): Promise<T | undefined> {
    return Promise.resolve(structuredClone(this.#kv.get(key)) as T | undefined);
  }

  put(key: string, value: unknown): Promise<void> {
    this.#kv.set(key, structuredClone(value));
    return Promise.resolve();
  }

  remove(key: string): Promise<void> {
    this.#kv.delete(key);
    return Promise.resolve();
  }

  addToOutbox(entry: OutboxEntry): Promise<void> {
    this.#outbox.set(entry.id, structuredClone(entry));
    return Promise.resolve();
  }

  outbox(): Promise<OutboxEntry[]> {
    return Promise.resolve(
      [...this.#outbox.values()]
        .map((e) => structuredClone(e))
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    );
  }

  removeFromOutbox(ids: readonly string[]): Promise<void> {
    for (const id of ids) this.#outbox.delete(id);
    return Promise.resolve();
  }
}
